import React, { useEffect, useState } from "react";
import { Box, useColorModeValue } from "@chakra-ui/react";
import { useHistory } from "react-router-dom";
import { useBillingContext } from "../../../contexts/BillingContext";
import GradientButton from "../Profile/components/GradientButton";

/**
 * Stripe Checkout success_url → `/billing/success?session_id=...`
 * Fullscreen over a blurred Dashboard preview (see Admin layout); refreshes billing for webhook lag.
 *
 * Future: repurpose as first-time onboarding / platform introduction for new subscribers.
 */
export default function BillingSuccess() {
  const { refreshWithRetry, isTier1Granted, status } = useBillingContext();
  const history = useHistory();
  const [syncing, setSyncing] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refreshWithRetry({ attempts: 6, delayMs: 2000 });
      } finally {
        if (!cancelled) setSyncing(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshWithRetry]);

  const goToDashboard = () => {
    history.push("/dashboard");
  };

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const muted = useColorModeValue("gray.600", "gray.400");
  const headingColor = useColorModeValue("gray.800", "white");

  return (
    <Box position="fixed" inset={0} zIndex={1000} display="flex" alignItems="center" justifyContent="center" p={4}>
      <Box position="absolute" inset={0} bg="blackAlpha.400" aria-hidden />
      <Box
        position="relative"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-success-heading"
        maxW="lg"
        w="full"
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="2xl"
        boxShadow="xl"
        p={{ base: 8, md: 10 }}
        textAlign="center"
      >
        <Box as="p" fontSize="sm" fontWeight="600" color="cyan.500" letterSpacing="0.06em" textTransform="uppercase" mb={3}>
          Payment successful
        </Box>
        <Box
          as="h1"
          id="billing-success-heading"
          fontSize="2xl"
          fontFamily="'Urbanist', sans-serif"
          color={headingColor}
          fontWeight="600"
          mb={4}
          lineHeight="short"
        >
          Your subscription is active — welcome to Tessera
        </Box>
        <Box as="p" color={muted} fontSize="md" lineHeight="tall" mb={6}>
          You&apos;re all set. Your Tessera access is starting now. Open the dashboard to sync your data, review performance, and use the full platform.
        </Box>

        {syncing && (
          <Box mb={6} color={muted} fontSize="sm">
            Confirming your subscription with our servers…
          </Box>
        )}

        {!syncing && !isTier1Granted && (
          <Box as="p" fontSize="sm" color="orange.500" mb={6}>
            Your payment went through. If the dashboard still asks you to subscribe, wait a few seconds and refresh — our
            systems may still be updating.
          </Box>
        )}

        <GradientButton size="lg" onClick={goToDashboard} showArrow={false}>
          Go to dashboard
        </GradientButton>
      </Box>
    </Box>
  );
}
